import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import AssigneeCombobox from "@/components/AssigneeCombobox";
import TaskAttachments from "@/components/TaskAttachments";
import { PriorityBadge, TagPills } from "@/lib/taskMeta";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { statusBadgeClasses, statusLabel, statusDotClass, formatDate } from "@/lib/statusColor";
import { toast } from "sonner";
import {
  ArrowLeft,
  Copy,
  Trash2,
  Calendar as CalIcon,
  CalendarIcon,
  User,
  Mail,
  Send,
  Pencil,
} from "lucide-react";

function toTimeInput(iso) {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [subtasks, setSubtasks] = useState([]);

  // edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [eTitle, setETitle] = useState("");
  const [eDesc, setEDesc] = useState("");
  const [eAssigneeName, setEAssigneeName] = useState("");
  const [eAssigneeEmail, setEAssigneeEmail] = useState("");
  const [eDeadlineDate, setEDeadlineDate] = useState();
  const [eDeadlineTime, setEDeadlineTime] = useState("17:00");
  const [ePriority, setEPriority] = useState("none");
  const [eTags, setETags] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [t, c] = await Promise.all([
        api.get(`/tasks/${id}`),
        api.get(`/tasks/${id}/comments`),
      ]);
      setTask(t.data);
      setSubtasks(t.data.subtasks || []);
      setComments(c.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, [id]);

  const shareUrl = task ? `${window.location.origin}/board/${task.share_token}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Task link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const authorName = (user && (user.name || user.email)) || "Admin";
      const { data } = await api.post(`/tasks/${id}/comments`, {
        author: authorName,
        text: text.trim(),
      });
      setComments((prev) => [...prev, data]);
      setText("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeTask = async () => {
    if (!window.confirm("Delete this task and all its comments?")) return;
    try {
      await api.delete(`/tasks/${id}`);
      toast.success("Task deleted");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openEdit = () => {
    if (!task) return;
    setETitle(task.title);
    setEDesc(task.description || "");
    setEAssigneeName(task.assignee_name);
    setEAssigneeEmail(task.assignee_email);
    const d = new Date(task.deadline);
    setEDeadlineDate(d);
    setEDeadlineTime(toTimeInput(task.deadline));
    setEPriority(task.priority || "none");
    setETags((task.tags || []).join(", "));
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!eDeadlineDate) {
      toast.error("Please pick a deadline date");
      return;
    }
    const [hh, mm] = eDeadlineTime.split(":");
    const dt = new Date(eDeadlineDate);
    dt.setHours(parseInt(hh, 10) || 17, parseInt(mm, 10) || 0, 0, 0);

    setSaving(true);
    try {
      const { data } = await api.patch(`/tasks/${id}`, {
        title: eTitle,
        description: eDesc,
        assignee_name: eAssigneeName,
        assignee_email: eAssigneeEmail,
        deadline: dt.toISOString(),
        priority: ePriority === "none" ? null : ePriority,
        tags: eTags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      setTask(data);
      setEditOpen(false);
      toast.success("Task updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 py-10">
          <p className="text-slate-500">Loading…</p>
        </main>
      </div>
    );
  }

  if (!task) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>

        <Card className="rounded-md border-slate-200 shadow-sm" data-testid="task-detail-card">
          <CardHeader className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <span className={`mt-2 w-2.5 h-2.5 rounded-full shrink-0 ${statusDotClass(task.color)}`} />
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {task.title}
                    </h1>
                    {task.parent_task_id && (
                      <Badge variant="outline" className="border-slate-300 text-xs">
                        Sub-task
                      </Badge>
                    )}
                  </div>
                  {task.assigned_by_email && (
                    <p className="text-xs text-slate-500 mt-1" data-testid="delegated-by">
                      Delegated by <span className="text-slate-800 font-medium">{task.assigned_by_email}</span>
                    </p>
                  )}
                  {task.description && (
                    <p className="text-slate-600 mt-2 leading-relaxed">{task.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <PriorityBadge priority={task.priority} />
                    <TagPills tags={task.tags} dataTestId="task-tags-pills" />
                  </div>
                </div>
              </div>
              <Badge variant="outline" className={`border ${statusBadgeClasses(task.color)}`} data-testid="task-status-badge">
                {statusLabel(task.color, task.status)}
              </Badge>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <MetaRow icon={<User size={14} />} label="Assigned to" value={task.assignee_name} />
              <MetaRow icon={<Mail size={14} />} label="Email" value={task.assignee_email} />
              <MetaRow icon={<CalIcon size={14} />} label="Assigned on" value={formatDate(task.assigned_at)} />
              <MetaRow icon={<CalIcon size={14} />} label="Deadline" value={formatDate(task.deadline)} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Separator />
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">Share link</p>
              <div className="flex gap-2">
                <Input readOnly value={shareUrl} className="font-mono text-xs" data-testid="share-link-input" />
                <Button variant="outline" onClick={copyLink} data-testid="copy-share-link-btn">
                  <Copy size={14} className="mr-1.5" /> Copy
                </Button>
              </div>
              <p className="text-xs text-slate-500">Send this link to {task.assignee_name}. They can view <strong>all</strong> tasks assigned to them (old and new), update status, and comment — all without logging in.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={openEdit} data-testid="edit-task-btn">
                <Pencil size={14} className="mr-1.5" /> Edit task
              </Button>
              <Button variant="outline" onClick={removeTask} data-testid="delete-task-btn" className="text-rose-700 hover:text-rose-800">
                <Trash2 size={14} className="mr-1.5" /> Delete task
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6">
          <Card className="rounded-md border-slate-200 shadow-sm p-5">
            <TaskAttachments taskId={id} scope="auth" canDelete={true} />
          </Card>
        </div>

        <div className="mt-8">
          <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            Comments
          </h2>
          <Card className="rounded-md border-slate-200 shadow-sm p-5">
            {comments.length === 0 ? (
              <p className="text-sm text-slate-500">No comments yet.</p>
            ) : (
              <ul className="space-y-4" data-testid="comments-list">
                {comments.map((c) => (
                  <li key={c.id} className="border-l-2 border-slate-200 pl-3">
                    <div className="text-sm">
                      <span className="font-medium text-slate-900">{c.author}</span>
                      <span className="text-slate-400 ml-2 text-xs">{formatDate(c.created_at)}</span>
                    </div>
                    <p className="text-slate-700 text-sm mt-1 leading-relaxed">{c.text}</p>
                  </li>
                ))}
              </ul>
            )}

            <form onSubmit={addComment} className="mt-4 space-y-2">
              <Textarea
                rows={3}
                placeholder="Add a comment…"
                value={text}
                onChange={(e) => setText(e.target.value)}
                data-testid="comment-input"
              />
              <div className="flex justify-end">
                <Button type="submit" disabled={!text.trim()} data-testid="add-comment-btn">
                  <Send size={14} className="mr-1.5" /> Post comment
                </Button>
              </div>
            </form>
          </Card>
        </div>

        {subtasks.length > 0 && (
          <div className="mt-8" data-testid="subtasks-section">
            <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
              Delegated sub-tasks ({subtasks.length})
            </h2>
            <p className="text-xs text-slate-500 mb-3">
              These are internal delegations {task.assignee_name} made from their board. Only you and the two people involved can see them.
            </p>
            <div className="space-y-2">
              {subtasks.map((s) => (
                <Link key={s.id} to={`/tasks/${s.id}`} className="block" data-testid={`subtask-${s.id}`}>
                  <Card className="p-4 rounded-md border-slate-200 shadow-sm hover:border-slate-300 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${statusDotClass(s.color)}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{s.title}</div>
                          <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span>{task.assignee_name} → <span className="text-slate-800 font-medium">{s.assignee_name}</span></span>
                            <span className="inline-flex items-center gap-1"><CalIcon size={11} /> Due {formatDate(s.deadline)}</span>
                          </div>
                        </div>
                      </div>
                      <Badge variant="outline" className={`border ${statusBadgeClasses(s.color)}`}>
                        {statusLabel(s.color, s.status)}
                      </Badge>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg" data-testid="edit-task-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>Edit task</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="etitle">Title</Label>
              <Input id="etitle" required value={eTitle} onChange={(e) => setETitle(e.target.value)} data-testid="edit-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edesc">Description</Label>
              <Textarea id="edesc" rows={3} value={eDesc} onChange={(e) => setEDesc(e.target.value)} data-testid="edit-description-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5 sm:col-span-2">
                <AssigneeCombobox
                  name={eAssigneeName}
                  email={eAssigneeEmail}
                  onChange={(n, em) => { setEAssigneeName(n); setEAssigneeEmail(em); }}
                  nameTestId="edit-assignee-name-input"
                  emailTestId="edit-assignee-email-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Deadline date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start font-normal"
                      type="button"
                      data-testid="edit-deadline-date-btn"
                    >
                      <CalendarIcon size={14} className="mr-2" />
                      {eDeadlineDate ? formatDate(eDeadlineDate).split(",")[0] : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto p-0">
                    <Calendar mode="single" selected={eDeadlineDate} onSelect={setEDeadlineDate} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="etime">Deadline time</Label>
                <Input id="etime" type="time" value={eDeadlineTime} onChange={(e) => setEDeadlineTime(e.target.value)} data-testid="edit-deadline-time-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Select value={ePriority} onValueChange={setEPriority}>
                  <SelectTrigger className="h-10" data-testid="edit-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="etags">Tags <span className="text-xs text-slate-400 font-normal">(comma-separated)</span></Label>
                <Input id="etags" value={eTags} onChange={(e) => setETags(e.target.value)} placeholder="urgent, invoice" data-testid="edit-tags-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="edit-save-btn">
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetaRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-slate-400 mt-0.5">{icon}</span>
      <div>
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-sm text-slate-900">{value}</div>
      </div>
    </div>
  );
}
